<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue';
import _ from 'lodash';
import type { USState, ValidationRules } from '@Shared';
import { FlowLayout, ActionBarLayout, US_STATES, useFormValidation, useFieldStatus, useValidateBank, useScroll } from '@Shared';
import { useCompanyStore } from '@Stores/company.store';
import { useLocationStore } from '@Stores/location.store';
import { useRequestSessionStore } from '@Stores/requestSession.store';
import useFlowNavigation from '@Use/useFlowNavigation';
import { useFlowSteps } from '@Use/useFlowSteps';
import useTaskNavTitle from '@Use/useTaskNavTitle';
import useCompanyImageBaseUrl from '@Use/useCompanyImageBaseUrl';
import useWiringInstructionsForm from '@Use/wiringInstructions/useWiringInstructionsForm';
import useAccountFormLabels from '@Use/wiringInstructions/useAccountFormLabels';
import usePossibleBrokerageWarning from '@Use/wiringInstructions/usePossibleBrokerageWarning';
import type { WiringInstructions } from '@Types/WiringInstrucions.interface';
import type { BankWiringData } from '@Types/BankWiringData.interface';
import { lookupBankApi } from '@Api/bankLookup.api';
import { isPossibleBrokerageDestinationApi } from '@Api/requestFlow.api';
import { EntityType } from '@Enums/entityType.enum';
import { AccountHolderType, ACCOUNT_HOLDER_TYPE_OPTIONS } from '@Enums/accountHolderType.enum';
import { BankAccountType, BANK_ACCOUNT_TYPE_OPTIONS } from '@Enums/bankAccountType.enum';
import { LINKS } from '@Constants/links.constants';
import CheckExampleGraphic from '@Assets/images/check-example.svg?inline';
// Components
import DividerAction from '@Components/shared/DividerAction.vue';
import BankLookup from '@Components/shared/BankLookup.vue';

type CollectFormState = WiringInstructions & {
    confirmCreditToAccountNumber: string | null;
    confirmFurtherCreditToAccountNumber: string | null;
};


// ─── Stores / navigation ──────────────────────────────────────────────────────

const companyStore = useCompanyStore();
const locationStore = useLocationStore();
const requestSessionStore = useRequestSessionStore();
const companyImageBaseUrl = useCompanyImageBaseUrl();
const { advanceFlow, goBackInFlow } = useFlowNavigation();
const { scrollToPageTop } = useScroll();
const { getProgressSteps, getCurrentStep } = useFlowSteps();
const steps = getProgressSteps();
const { taskNavTitle } = useTaskNavTitle();


// ─── Form state ───────────────────────────────────────────────────────────────

const { createEmptyWiringInstructions, sanitizeAccountName, sanitizeAccountNumber } = useWiringInstructionsForm();

const wiringInstructions = ref<CollectFormState>({
    ...createEmptyWiringInstructions(),
    confirmCreditToAccountNumber: null,
    confirmFurtherCreditToAccountNumber: null,
});
const selectedAccountType = ref<{ value: BankAccountType; display: string } | null>(null);
const selectedAccountHolderType = ref<{ value: AccountHolderType; display: string }>(ACCOUNT_HOLDER_TYPE_OPTIONS[0]);
const selectedState = ref<USState | null>(null);
const jointAccountHolders = ref<string[]>([]);
const furtherInitialState = ref<string | null>(null);
const furtherCollapse = ref<{ isOpen: boolean; toggle: () => void } | null>(null);
const bankWiringData = ref<BankWiringData | null>(null);
const bankLoading = ref(false);


// ─── Validation ───────────────────────────────────────────────────────────────

// PLAT-3157: the holder-name requirement follows the selected type, which the shared validator's
// static rule vocabulary cannot express — and mutating/replacing the rules ref races its async
// validation loop (it snapshots the key list, awaits per key, and trips on a removed entry). The
// three holder fields are therefore validated locally (see holderFieldError below); the shared
// rules object stays untouched for the rest of the form.
const buildValidationRules = (): ValidationRules => ({
    'beneficiaryBank': [
        { required: true, property: 'routingNumber', message: 'Routing number is required' },
        { validateAbaRoutingNumber: true, property: 'routingNumber', message: 'Valid routing number is required' },
        { validateBank: true, immediate: true, transactableOnly: true, message: 'Invalid routing number' },
    ],
    'bankAccountInfo.accountType': [{ required: true, message: 'Account type is required' }],
    'creditTo.accountNumber': [
        { required: true, message: 'Account number is required' },
        { validateAccountNumber: true, message: 'Account number must be at least 4 digits' },
    ],
    'personalAddress.address1': [{ required: true, message: 'Address is required' }],
    'personalAddress.city': [{ required: true, message: 'City is required' }],
    'personalAddress.state': [{ required: true, message: 'State is required' }],
    'personalAddress.postalCode': [
        { required: true, message: 'Zip code is required' },
        { validateZipCode: true, message: 'Valid zip code is required' },
    ],
    'furtherCreditTo.accountName': [
        { requiredWhenOneProvided: ['furtherCreditTo.accountNumber'], message: 'Account holder is required' },
    ],
    'furtherCreditTo.accountNumber': [
        { validateAccountNumber: true, message: 'Account number must be at least 4 digits' },
    ],
    'confirmCreditToAccountNumber': [
        { required: true, message: 'Please confirm the account number' },
        { validateFieldsMatch: true, field1: 'creditTo.accountNumber', field2: 'confirmCreditToAccountNumber', message: 'Account numbers must match' },
    ],
    'confirmFurtherCreditToAccountNumber': [
        { requiredWhenOneProvided: ['furtherCreditTo.accountNumber'], message: 'Please confirm the account number' },
        { validateFieldsMatch: true, field1: 'furtherCreditTo.accountNumber', field2: 'confirmFurtherCreditToAccountNumber', message: 'Account numbers must match' },
    ],
});

const validationRules = ref<ValidationRules>(buildValidationRules());

// ─── Account holder validation (local, type-dependent) ───────────────────────

const holderValidationActive = ref(false);

const holderRequiredFields = computed<Record<string, string>>(() =>
    selectedAccountHolderType.value.value === AccountHolderType.Business
        ? { accountHolderBusinessName: 'Account holder name is required' }
        : {
            accountHolderFirstName: 'First name is required',
            accountHolderLastName: 'Last name is required',
        });

const holderErrors = computed<Record<string, string>>(() => {
    if (!holderValidationActive.value) return {};
    const creditTo = wiringInstructions.value.creditTo as unknown as Record<string, unknown>;
    return _.pickBy(
        holderRequiredFields.value,
        (_message, field) => !String(creditTo[field] ?? '').trim(),
    );
});

const hasHolderError = computed(() => !_.isEmpty(holderErrors.value));
const holderFieldStatus = (field: string) => holderValidationActive.value
    ? (holderErrors.value[field] ? 'error' : 'valid')
    : null;
const holderFieldMessage = (field: string) => holderErrors.value[field] ?? '';
const formValidation = useFormValidation(wiringInstructions, validationRules);
const { turnOnValidation, hasError } = formValidation;
const { getFieldStatus, getFieldMessage } = useFieldStatus(formValidation);


// ─── Computed ─────────────────────────────────────────────────────────────────

const entityType = computed(() => requestSessionStore.requestSummary?.recipientEntityType ?? null);
const accountType = computed(() => selectedAccountType.value?.value ?? null);

const routingNumberFieldStatus = computed(() => {
    const validationStatus = getFieldStatus('beneficiaryBank');
    if (validationStatus === 'error') return 'error';
    if (wiringInstructions.value.beneficiaryBank.name) return 'success';
    return validationStatus;
});

const {
    isInvestment,
    isTrustOrEstate,
    accountNumberFocusMessage,
    accountBillingDescription,
    finalCreditToTitle,
} = useAccountFormLabels(entityType, accountType);

const brokerageSubtext = computed(() =>
    'If your brokerage account requires final credit to information, please provide that in the section highlighted at the end of this form.'
);

// Any one field counts as "filled" here on purpose — the PDF templates instead keep warning until
// Final Credit To is complete (name AND number), and that asymmetry is intended, not a bug to fix.
const furtherCreditToFilled = computed(() =>
    !isActualEmpty(wiringInstructions.value.furtherCreditTo.accountName)
    || !isActualEmpty(wiringInstructions.value.furtherCreditTo.accountNumber)
    || !isActualEmpty(wiringInstructions.value.confirmFurtherCreditToAccountNumber)
);

const isBusinessHolder = computed(() =>
    selectedAccountHolderType.value.value === AccountHolderType.Business
);

// The Business branch overrides the composable's copy; entity/investment nuances stay for Individual.
const accountBillingDescriptionResolved = computed(() =>
    isBusinessHolder.value
        ? 'Please use the registered business address associated with the account.'
        : accountBillingDescription.value
);

// Keeps the legacy single-string name continuously in sync with the structured capture, so the
// brokerage preview, the review screen, and every downstream consumer keep reading one field.
const syncPrimaryHolderName = () => {
    const creditTo = wiringInstructions.value.creditTo;
    creditTo.accountName = isBusinessHolder.value
        ? (creditTo.accountHolderBusinessName ?? '')
        : [creditTo.accountHolderFirstName, creditTo.accountHolderLastName]
            .map(part => (part ?? '').trim())
            .filter(Boolean)
            .join(' ');
};

const combinedHolderName = computed(() => {
    const primary = wiringInstructions.value.creditTo.accountName;
    if (isBusinessHolder.value) return primary;
    const others = _.filter(jointAccountHolders.value, s => !isActualEmpty(s));
    return [primary, ...others].join(', ');
});


// ─── Helpers ──────────────────────────────────────────────────────────────────

const isActualEmpty = (input: unknown): boolean => _.isEmpty(input) && !_.isNumber(input);

// ─── Bank lookup ──────────────────────────────────────────────────────────────

const { isRoutingNumberValid } = useValidateBank();

const resetBankData = (routingNumber: string) => {
    const empty = createEmptyWiringInstructions();
    bankWiringData.value = null;
    wiringInstructions.value.beneficiaryBank = { ...empty.beneficiaryBank, routingNumber };
    wiringInstructions.value.correspondentBank = empty.correspondentBank;
};

const lookupBank = async (routingNumber: string) => {
    if (!isRoutingNumberValid(routingNumber)) {
        resetBankData(routingNumber);
        return;
    }
    try {
        bankLoading.value = true;
        const result = await lookupBankApi(routingNumber);
        if (!result?.beneficiaryBank?.name) {
            resetBankData(routingNumber);
            return;
        }
        Object.assign(wiringInstructions.value, result);
        bankWiringData.value = result;
    } catch (error) {
        console.error(error);
        resetBankData(routingNumber);
    } finally {
        bankLoading.value = false;
    }
};

const onRoutingNumberInput = (val: string) => {
    const clean = val.replace(/\D/g, '');
    wiringInstructions.value.beneficiaryBank.routingNumber = clean;
    lookupBank(clean);
};


// ─── Possible brokerage detection ─────────────────────────────────────────────

const brokerageDetectionEnabled = ref(companyStore.brokerageDetectionEnabled);

// The detection engine lives in usePossibleBrokerageWarning; the view feeds it the
// destination getters, the preview API, the routing-number validator, and the flag,
// and owns the two view-level concerns the engine deliberately doesn't: opening the
// Final Credit To collapse on a positive result, and gating the first evaluation to
// onMounted (below) so the watch reacts only to genuine user edits.
const {
    possibleBrokerage,
    previewStatus,
    previewFailed,
    preventSubmit,
    kickBrokerageCheck,
} = usePossibleBrokerageWarning({
    getRoutingNumber: () => wiringInstructions.value.beneficiaryBank.routingNumber,
    getAccountName: () => wiringInstructions.value.creditTo.accountName,
    previewApi: isPossibleBrokerageDestinationApi,
    isRoutingNumberValid,
    isEnabled: () => brokerageDetectionEnabled.value,
    // Reveal the fields the warning points at, so the client doesn't have to find
    // the collapse themselves. Still only a suggestion — nothing forces them to fill it in.
    onPossibleBrokerage: () => {
        if (furtherCollapse.value) furtherCollapse.value.isOpen = true;
    },
});

// onMounted restores the form (mutating the watched sources) and owns the first
// evaluation, including the pre-flag 'pending' block. Ignore that initial fire so
// it can't reset the gate to 'idle' while the flag is still resolving; react only
// to genuine user edits afterwards.
let hasInitialized = false;

// Separate getters, so an unrelated write that replaces the beneficiaryBank
// object (the bank lookup does this) doesn't re-fire the check.
watch(
    [
        () => wiringInstructions.value.beneficiaryBank.routingNumber,
        () => wiringInstructions.value.creditTo.accountName,
    ],
    () => {
        if (hasInitialized) kickBrokerageCheck();
    },
);


// ─── Event handlers ───────────────────────────────────────────────────────────

const onAccountTypeSelect = (option: { value: BankAccountType; display: string } | null) => {
    selectedAccountType.value = option;
    wiringInstructions.value.bankAccountInfo.accountType = option?.value ?? null;

    if (!furtherCollapse.value) return;
    if (option?.value === BankAccountType.Investment) {
        furtherCollapse.value.isOpen = true;
    } else if (!furtherCreditToFilled.value) {
        furtherCollapse.value.isOpen = false;
    }
};

const onStateSelect = (state: USState) => {
    selectedState.value = state;
    wiringInstructions.value.personalAddress.state = state.abbreviation;
};

// Browser/OS address autofill (iOS Safari AutoFill, Chrome Autofill) writes
// only into native form controls. The visible state field is a div-based
// AppDropdown, so we bridge it with a visually-hidden native <select> that
// carries autocomplete="address-level1". When autofill fires, we route the
// chosen value through onStateSelect so form state + validation stay in sync.
//
// Match both abbreviation and full name — iOS / Chrome write the option's
// `value`, but some password managers write the full state name and rely on
// option-text matching. Cover both paths so validation stays in sync.
const onNativeStateAutofill = (event: Event) => {
    const written = (event.target as HTMLSelectElement).value;
    if (!written) return;
    const writtenLower = written.toLowerCase();
    if (writtenLower === selectedState.value?.abbreviation.toLowerCase()) return;
    const match = US_STATES.find(
        s => s.abbreviation.toLowerCase() === writtenLower || s.name.toLowerCase() === writtenLower
    );
    if (match) onStateSelect(match);
};

const onHolderFirstNameInput = (val: string) => {
    wiringInstructions.value.creditTo.accountHolderFirstName = sanitizeAccountName(val);
    syncPrimaryHolderName();
};

const onHolderLastNameInput = (val: string) => {
    wiringInstructions.value.creditTo.accountHolderLastName = sanitizeAccountName(val);
    syncPrimaryHolderName();
};

const onHolderBusinessNameInput = (val: string) => {
    wiringInstructions.value.creditTo.accountHolderBusinessName = sanitizeAccountName(val);
    syncPrimaryHolderName();
};

const onAccountHolderTypeSelect = (option: { value: AccountHolderType; display: string }) => {
    if (!option || option.value === selectedAccountHolderType.value.value) return;
    selectedAccountHolderType.value = option;
    const creditTo = wiringInstructions.value.creditTo;
    creditTo.accountHolderType = option.value;

    // Switching types clears the other type's values so a submission never carries both.
    if (option.value === AccountHolderType.Business) {
        creditTo.accountHolderFirstName = null;
        creditTo.accountHolderLastName = null;
        jointAccountHolders.value = [];
    } else {
        creditTo.accountHolderBusinessName = null;
    }

    syncPrimaryHolderName();
};

const onAccountNumberInput = (val: string) => {
    wiringInstructions.value.creditTo.accountNumber = sanitizeAccountNumber(val);
};

const onFurtherHolderNameInput = (val: string) => {
    wiringInstructions.value.furtherCreditTo.accountName = sanitizeAccountName(val);
};

const onFurtherAccountNumberInput = (val: string) => {
    wiringInstructions.value.furtherCreditTo.accountNumber = sanitizeAccountNumber(val);
};

const addJointAccountHolder = () => {
    if (jointAccountHolders.value.length >= 5) return;
    jointAccountHolders.value.push('');
};

const deleteJointAccountHolder = (index: number) => {
    if (jointAccountHolders.value.length > index) {
        jointAccountHolders.value.splice(index, 1);
    }
};

const onCloseFurtherCredit = () => {
    wiringInstructions.value.furtherCreditTo.accountNumber = '';
    wiringInstructions.value.furtherCreditTo.accountName = '';
    wiringInstructions.value.confirmFurtherCreditToAccountNumber = null;
};


// ─── Continue ─────────────────────────────────────────────────────────────────

const onContinue = async () => {
    // Fail-fast gate: a pending or failed brokerage check leaves the destination
    // unverified, so block continuing until it resolves successfully.
    if (preventSubmit.value) return;

    holderValidationActive.value = true;
    await turnOnValidation(true);

    if (!hasError.value && !hasHolderError.value) {
        const filteredHolders = isBusinessHolder.value ? [] : _.filter(jointAccountHolders.value, s => !isActualEmpty(s));
        const primaryHolderName = wiringInstructions.value.creditTo.accountName ?? '';
        // Keep the pieces that were joined, so a comma inside one name is not treated as another holder on the way back.
        requestSessionStore.accountHolders = isBusinessHolder.value
            ? []
            : [primaryHolderName, ...filteredHolders];
        wiringInstructions.value.bankAccountInfo.isJoint = filteredHolders.length > 0;
        wiringInstructions.value.creditTo.accountHolderType = selectedAccountHolderType.value.value;
        wiringInstructions.value.creditTo.accountName = combinedHolderName.value;

        const instructionsToSave = _.omit(wiringInstructions.value, ['confirmCreditToAccountNumber', 'confirmFurtherCreditToAccountNumber']);
        requestSessionStore.collectWiringInstructions = _.cloneDeep(instructionsToSave as WiringInstructions);
        advanceFlow();
        scrollToPageTop();
    }
};


// ─── Lifecycle ────────────────────────────────────────────────────────────────

onMounted(() => {
    const stored = requestSessionStore.collectWiringInstructions;
    const hasStoredData = !_.isEmpty(stored.creditTo.accountName) || !_.isEmpty(stored.creditTo.accountNumber);
    if (hasStoredData) {
        wiringInstructions.value = { ..._.cloneDeep(stored), confirmCreditToAccountNumber: null, confirmFurtherCreditToAccountNumber: null };
    } else {
        wiringInstructions.value.bankAccountInfo.isJoint = false;
        wiringInstructions.value.bankAccountInfo.isTrust = entityType.value === EntityType.Trust;
    }

    // Restore the account holder type; absent (legacy/stored-before-PLAT-3157) defaults to Individual.
    const storedHolderType = wiringInstructions.value.creditTo.accountHolderType;
    selectedAccountHolderType.value = ACCOUNT_HOLDER_TYPE_OPTIONS.find(o => o.value === storedHolderType)
        ?? ACCOUNT_HOLDER_TYPE_OPTIONS[0];
    wiringInstructions.value.creditTo.accountHolderType = selectedAccountHolderType.value.value;

    // Refill from the names saved on Continue. Do not split accountName on commas —
    // a single name such as "Sammy Davis, Jr" would come back as two holders.
    const storedHolders = requestSessionStore.accountHolders;
    if (!isBusinessHolder.value && storedHolders.length > 0) {
        wiringInstructions.value.creditTo.accountName = storedHolders[0] ?? '';
        jointAccountHolders.value = storedHolders.slice(1);
    }

    // Restore selectedAccountType first (so isInvestment is correct when setting furtherInitialState)
    if (wiringInstructions.value.bankAccountInfo.accountType) {
        selectedAccountType.value = BANK_ACCOUNT_TYPE_OPTIONS.find(
            o => o.value === wiringInstructions.value.bankAccountInfo.accountType
        ) ?? null;
    }

    // Restore selectedState
    if (wiringInstructions.value.personalAddress.state) {
        selectedState.value = US_STATES.find(
            s => s.abbreviation === wiringInstructions.value.personalAddress.state
        ) ?? null;
    }

    // Set furtherInitialState after restoring account type so isInvestment reflects the stored state
    const hasFurtherData = !isActualEmpty(wiringInstructions.value.furtherCreditTo.accountName)
        || !isActualEmpty(wiringInstructions.value.furtherCreditTo.accountNumber);
    furtherInitialState.value = (hasFurtherData || isInvestment.value) ? 'opened' : 'closed';

    if (brokerageDetectionEnabled.value && isRoutingNumberValid(wiringInstructions.value.beneficiaryBank.routingNumber)) {
        previewStatus.value = 'pending';
    }

    hasInitialized = true;
    if (!brokerageDetectionEnabled.value) return;

    // Flag on: evaluate any pre-filled destination the restore left in place.
    kickBrokerageCheck();
});
</script>


<template>
    <flow-layout
        is-white-label
        :company="companyStore.company"
        :brand="companyStore.brand"
        :location="locationStore.location"
        :company-image-base-url="companyImageBaseUrl"
        :steps="steps"
        :current-step="getCurrentStep()"
        show-back-button
        @back-click="goBackInFlow"
    >
        <template v-if="taskNavTitle" #header>{{ taskNavTitle }}</template>

        <app-form-content-layout class="collect-account-details">
            <h1 bottomOffset="1rem">Enter account details</h1>
            <p>Please provide the bank account details where the money should be deposited.</p>

            <img
                :src="CheckExampleGraphic"
                class="collect-account-details__check-example"
                alt="Routing number location" />

            <p class="small dim">
                The routing and account numbers on your checks may differ from the numbers needed for a wire.
                Please double check with your financial institution.
            </p>

            <app-form-content-layout class="collect-account-details__shaded">
                <div class="collect-account-details__section-header">Bank information</div>

                <app-masked-input
                    :model-value="wiringInstructions.beneficiaryBank.routingNumber"
                    @update:model-value="onRoutingNumberInput"
                    label="Routing number"
                    mask="#########"
                    is-required
                    input-mode="numeric"
                    :field-status="routingNumberFieldStatus"
                    :message="getFieldMessage('beneficiaryBank')"
                    data-id="account-routing-number"
                >
                    <template #focus-message>
                        <span v-if="!bankWiringData?.beneficiaryBank?.name">
                            This is nine digits, and can be found on a check or bank statement,
                            or by logging into the online banking system associated with the account.
                        </span>
                    </template>
                </app-masked-input>

                <bank-lookup
                    v-if="bankWiringData || bankLoading"
                    :bank-wiring-data="bankWiringData"
                    :is-loading="bankLoading" />

                <!-- The live region has to already be in the DOM when the warning
                     appears, or screen readers won't announce it — so the wrapper
                     stays rendered and only the notice inside is conditional. -->
                <div aria-live="polite" data-id="possible-brokerage-live-region">
                    <div
                        v-if="brokerageDetectionEnabled && possibleBrokerage && !furtherCreditToFilled"
                        class="collect-account-details__brokerage-notice"
                        data-id="possible-brokerage-warning"
                    >
                        <app-icon icon="cd-ic-info-circle" class="collect-account-details__brokerage-notice-icon" />
                        <p class="collect-account-details__brokerage-notice-text">
                            It looks like you're using a brokerage account. If your brokerage account requires final credit to information, please provide that in the <strong>Final Credit To</strong> section at the end of this form.
                        </p>
                    </div>
                </div>

                <!-- Native section rule separating the notice from the routing help link; shown only with the warning. -->
                <hr
                    v-if="brokerageDetectionEnabled && possibleBrokerage && !furtherCreditToFilled"
                    class="collect-account-details__brokerage-divider"
                />

                <!-- Fail-fast: distinct from the advisory info notice above —
                     a blocking error when the destination couldn't be verified. -->
                <app-banner-notice
                    v-if="brokerageDetectionEnabled && previewFailed"
                    type="alert"
                    data-id="possible-brokerage-error"
                >
                    We couldn't verify this destination account right now. Please check
                    the routing number and try again before continuing.
                </app-banner-notice>

                <div class="collect-account-details__sub-link">
                    <app-link
                        type="external"
                        :link-url="LINKS.CertifID.RoutingNumberHelp"
                        data-id="routing-number-help-link"
                    >Not sure which routing number to use?</app-link>
                </div>

                <app-dropdown
                    :model-value="selectedAccountType"
                    @update:model-value="onAccountTypeSelect"
                    :options="BANK_ACCOUNT_TYPE_OPTIONS"
                    id-field="value"
                    display-field="display"
                    label="Account type"
                    is-required
                    :field-status="getFieldStatus('bankAccountInfo.accountType')"
                    :message="getFieldMessage('bankAccountInfo.accountType')"
                    data-id="account-type"
                />

                <app-dropdown
                    :model-value="selectedAccountHolderType"
                    @update:model-value="onAccountHolderTypeSelect"
                    :options="ACCOUNT_HOLDER_TYPE_OPTIONS"
                    id-field="value"
                    display-field="display"
                    label="Account holder type"
                    is-required
                    data-id="account-holder-type"
                >
                    <template #item="{ item }">
                        <div class="account-holder-type-item">
                            <div :class="{ 'account-holder-type-item__label': true, 'account-holder-type-item__label--selected': item?.value === selectedAccountHolderType.value }">
                                {{ item?.display }}
                            </div>
                            <div>{{ item?.description }}</div>
                        </div>
                    </template>
                </app-dropdown>

                <template v-if="!isBusinessHolder">
                    <app-text-input
                        key="holder-first-name"
                        :model-value="wiringInstructions.creditTo.accountHolderFirstName ?? ''"
                        @update:model-value="onHolderFirstNameInput"
                        label="Account holder first name"
                        is-required
                        :maxlength="74"
                        :field-status="holderFieldStatus('accountHolderFirstName')"
                        :message="holderFieldMessage('accountHolderFirstName')"
                        data-id="account-holder-first-name"
                    >
                        <template #focus-message>
                            <span v-if="!wiringInstructions.creditTo.accountHolderFirstName">Enter the name exactly as it appears on the bank account, matching a government-issued ID.</span>
                        </template>
                    </app-text-input>
                    <app-text-input
                        key="holder-last-name"
                        :model-value="wiringInstructions.creditTo.accountHolderLastName ?? ''"
                        @update:model-value="onHolderLastNameInput"
                        label="Account holder last name"
                        is-required
                        :maxlength="74"
                        :field-status="holderFieldStatus('accountHolderLastName')"
                        :message="holderFieldMessage('accountHolderLastName')"
                        data-id="account-holder-last-name"
                    />
                </template>

                <app-text-input
                    v-if="isBusinessHolder"
                    key="holder-business-name"
                    :model-value="wiringInstructions.creditTo.accountHolderBusinessName ?? ''"
                    @update:model-value="onHolderBusinessNameInput"
                    label="Account holder name"
                    is-required
                    :maxlength="150"
                    :field-status="holderFieldStatus('accountHolderBusinessName')"
                    :message="holderFieldMessage('accountHolderBusinessName')"
                    data-id="account-holder-business-name"
                >
                    <template #focus-message>
                        <span v-if="!wiringInstructions.creditTo.accountHolderBusinessName">Enter the full legal entity name as registered with the bank, including any suffix such as LLC, Inc or Trust.</span>
                    </template>
                </app-text-input>

                <template v-if="!isBusinessHolder && !isTrustOrEstate">
                    <app-text-input
                        v-for="(_, index) in jointAccountHolders"
                        :key="'joint_' + index"
                        v-model="jointAccountHolders[index]"
                        :label="index === 0 ? 'Joint account holder' + (jointAccountHolders.length > 1 ? 's' : '') : ''"
                        :maxlength="150"
                        :data-id="'joint-account-holder-' + index"
                    >
                        <template #after-input>
                            <app-button
                                type="transparent"
                                icon="cd-ic-trash"
                                @on-click="deleteJointAccountHolder(index)"
                            />
                        </template>
                        <template #focus-message>
                            <span v-if="!jointAccountHolders[index]">Please provide all names listed on this account.</span>
                        </template>
                    </app-text-input>

                    <divider-action
                        v-if="jointAccountHolders.length < 5"
                        data-id="add-joint-holder"
                        @click="addJointAccountHolder"
                    >
                        Add joint account holder
                    </divider-action>
                </template>

                <app-text-input
                    key="account-number"
                    :model-value="wiringInstructions.creditTo.accountNumber"
                    @update:model-value="onAccountNumberInput"
                    label="Account number"
                    is-required
                    :maxlength="26"
                    input-mode="text"
                    :field-status="getFieldStatus('creditTo.accountNumber')"
                    :message="getFieldMessage('creditTo.accountNumber')"
                    data-id="account-number"
                >
                    <template #focus-message>
                        <span v-if="!wiringInstructions.creditTo.accountNumber">{{ accountNumberFocusMessage }}</span>
                    </template>
                </app-text-input>

                <app-text-input
                    key="account-number-confirm"
                    v-model="wiringInstructions.confirmCreditToAccountNumber"
                    label="Confirm account number"
                    is-required
                    :maxlength="26"
                    input-mode="text"
                    :field-status="getFieldStatus('confirmCreditToAccountNumber')"
                    :message="getFieldMessage('confirmCreditToAccountNumber')"
                    data-id="account-number-confirm"
                />

                <div class="collect-account-details__sub-link">
                    <app-tooltip
                        tip="Credit Union Accounts: The account number on your check may not be the one used for wires. To avoid delays, please check with your bank before you continue."
                    >
                        <app-link type="external" data-id="credit-union-link">Using a Credit Union account?</app-link>
                    </app-tooltip>
                </div>
            </app-form-content-layout>

            <h1 borderOffset="1rem">Account billing address</h1>
            <p>{{ accountBillingDescriptionResolved }}</p>

            <app-form-content-layout class="collect-account-details__shaded">

                <app-text-input
                    v-model="wiringInstructions.personalAddress.address1"
                    label="Address line"
                    is-required
                    :field-status="getFieldStatus('personalAddress.address1')"
                    :message="getFieldMessage('personalAddress.address1')"
                    data-id="address1"
                />

                <app-text-input
                    v-model="wiringInstructions.personalAddress.address2"
                    label="Apartment, suite, etc."
                    data-id="address2"
                />

                <app-text-input
                    v-model="wiringInstructions.personalAddress.city"
                    label="City"
                    is-required
                    :field-status="getFieldStatus('personalAddress.city')"
                    :message="getFieldMessage('personalAddress.city')"
                    data-id="city"
                />

                <div class="state-field">
                    <app-dropdown
                        :model-value="selectedState"
                        @update:model-value="onStateSelect"
                        :options="US_STATES"
                        id-field="abbreviation"
                        display-field="name"
                        label="State"
                        is-required
                        :field-status="getFieldStatus('personalAddress.state')"
                        :message="getFieldMessage('personalAddress.state')"
                        data-id="state"
                    />
                    <select
                        name="state"
                        autocomplete="address-level1"
                        aria-hidden="true"
                        tabindex="-1"
                        class="state-autofill-mirror"
                        :value="selectedState?.abbreviation ?? ''"
                        @change="onNativeStateAutofill"
                        @input="onNativeStateAutofill">
                        <option value=""></option>
                        <option
                            v-for="s in US_STATES"
                            :key="s.abbreviation"
                            :value="s.abbreviation">
                            {{ s.name }}
                        </option>
                    </select>
                </div>

                <app-masked-input
                    v-model="wiringInstructions.personalAddress.postalCode"
                    label="Zip code"
                    mask="#####"
                    is-required
                    input-mode="numeric"
                    :field-status="getFieldStatus('personalAddress.postalCode')"
                    :message="getFieldMessage('personalAddress.postalCode')"
                    data-id="zip-code"
                />
            </app-form-content-layout>

            <app-collapse
                v-if="furtherInitialState !== null"
                ref="furtherCollapse"
                :initial-state="furtherInitialState"
                class="collect-account-details__collapse"
                :class="{ 'collect-account-details__collapse--no-trigger': isInvestment }"
                @on-close="onCloseFurtherCredit"
            >
                <template #trigger="slotProps">
                    <div
                        class="collect-account-details__collapse-trigger"
                        :class="{ 'collect-account-details__collapse-trigger--open': slotProps?.isOpen }"
                    >
                        <span>Add final credit to </span>
                        <span class="collect-account-details__collapse-divider"></span>
                        <app-icon
                            icon="cd-ic-caret-down"
                            :class="{ 'collect-account-details__caret--open': slotProps?.isOpen }"
                        />
                    </div>
                </template>

                <app-form-content-layout class="collect-account-details__shaded">
                    <div v-if="finalCreditToTitle" class="collect-account-details__section-header">
                        {{ finalCreditToTitle }}
                    </div>

                    <p v-if="possibleBrokerage" class="collect-account-details__further-subtext">
                        {{ brokerageSubtext }}
                    </p>

                    <app-text-input
                        key="further-account-holder"
                        :model-value="wiringInstructions.furtherCreditTo.accountName"
                        @update:model-value="onFurtherHolderNameInput"
                        label="Account holder"
                        :maxlength="150"
                        :field-status="getFieldStatus('furtherCreditTo.accountName')"
                        :message="getFieldMessage('furtherCreditTo.accountName')"
                        data-id="further-account-holder"
                    />

                    <app-text-input
                        key="further-account-number"
                        :model-value="wiringInstructions.furtherCreditTo.accountNumber"
                        @update:model-value="onFurtherAccountNumberInput"
                        label="Account number"
                        :maxlength="26"
                        input-mode="text"
                        :field-status="getFieldStatus('furtherCreditTo.accountNumber')"
                        :message="getFieldMessage('furtherCreditTo.accountNumber')"
                        data-id="further-account-number"
                    />

                    <app-text-input
                        key="further-account-number-confirm"
                        v-model="wiringInstructions.confirmFurtherCreditToAccountNumber"
                        label="Confirm account number"
                        :maxlength="26"
                        input-mode="text"
                        :field-status="getFieldStatus('confirmFurtherCreditToAccountNumber')"
                        :message="getFieldMessage('confirmFurtherCreditToAccountNumber')"
                        data-id="further-account-number-confirm"
                    />
                </app-form-content-layout>
            </app-collapse>
        </app-form-content-layout>

        <action-bar-layout @back-click="goBackInFlow">
            <app-button
                expanded
                size="large"
                :is-disabled="preventSubmit"
                @on-click="onContinue"
                data-id="account-continue"
            >
                Continue
            </app-button>
        </action-bar-layout>
    </flow-layout>
</template>


<style lang="scss" scoped>
@use "certifid-v2-client-hub-styles" as *;

.state-field {
    position: relative;
}

// Hidden native <select> that picks up browser/OS address autofill on
// behalf of the visible AppDropdown above it. Positioned off-screen
// rather than visually-hidden — iOS Safari's autofill skips fields that
// look "too invisible" (opacity:0 + pointer-events:none + clip-path
// stacked together is read as "fake field"). Off-screen positioning
// keeps it real-to-autofill while invisible to sight.
.state-autofill-mirror {
    position: absolute;
    left: -10000px;
    width: 1px;
    height: 1px;
    overflow: hidden;
}

.collect-account-details {

    p {
        line-height: 150%;

        &.small {
            font-size: 0.9rem
        }

        &.dim {
            color: $black-low;
        }
    }

    &__brokerage-notice {
        display: flex;
        gap: 0.75rem;
        padding: 1rem;
        background-color: #f5f5f5;
        border-radius: 0.375rem;
        align-items: flex-start;
    }

    &__brokerage-notice-icon {
        flex-shrink: 0;
        color: #555;
        margin-top: 0.125rem;
    }

    &__brokerage-notice-text {
        margin: 0;
        font-size: 0.875rem;
        line-height: 150%;
        color: #555;
    }

    &__further-subtext {
        margin: 0 0 1rem 0;
        font-size: 0.875rem;
        line-height: 150%;
        color: #777;
    }

    &__shaded {
        background-color: $gray-light;
        padding: 1rem;
        border-radius: 0.375rem;

        :deep(.app-columns) {
            &:not(:last-child) {
                margin-bottom: 1rem;
            }

            &.layout-h-divider {
                padding-bottom: 1rem;
            }
        }
    }

    &__sub-link {
        display: flex;
        justify-content: center;
        font-size: 0.9rem;
    }

    &__check-example {
        margin: 0 4rem;
    }

    &__section-header {
        font-size: 1.25rem;
        font-weight: 700;
        line-height: 150%;
        color: $black-high;
    }

    &__section-description,
    &__check-notes {
        font-size: 0.875rem;
        line-height: 150%;
        color: $black-low;
        margin: 0;
    }

    &__check-notes {
        text-align: center;
    }

    &__collapse-trigger {
        display: flex;
        align-items: center;
        width: 100%;
        gap: 0.5rem;
        font-size: 0.875rem;
        color: $black-med;
        cursor: pointer;
    }

    &__collapse-divider {
        display: block;
        flex: 1;
        height: 1px;
        background-color: $dark-gray;
    }

    &__brokerage-divider {
        border: none;
        height: 1px;
        background-color: $dark-gray;
        margin: 1rem 0;
    }

    &__collapse-trigger--open {
        color: $theme-primary;

        .collect-account-details__collapse-divider {
            background-color: $theme-primary;
        }
    }

    &__caret--open {
        transform: rotate(180deg);
    }

    &__collapse {
        gap: 1rem;
    }

    &__collapse--no-trigger :deep(.trigger) {
        display: none;
    }

}

.account-holder-type-item {
    // uilib's .app-dropdown-item sets white-space: nowrap; reset it so the
    // description sentences can wrap instead of being clipped on narrow screens
    white-space: normal;
    font-size: 1rem;
    line-height: 150%;

    &__label {
        color: $theme-primary;
        font-weight: 700;

        &--selected {
            color: $white;
        }
    }
}
</style>
